import type { SubmitHandler } from "react-hook-form"

import { FILE_UPLOAD_TAKES_TIME_MESSAGE } from "@/components/upload/constants"
import type {
  CompetitorProduct,
  CompetitorProductCreateInput,
  CompetitorProductCreateSuccess,
  CompetitorProductUpdateInput,
  CompetitorProductUpdateSuccess,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useCompetitorProduct } from "@/screens/ManageProducts/components/CompetitorProducts/hooks/useCompetitorProduct"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCompetitorProductActionsReturn {
  createCompetitorProductData: SubmitHandler<CompetitorProductCreateInput>
  updateCompetitorProductData: SubmitHandler<CompetitorProductUpdateInput>
  deleteCompetitorProductData: (competitorProductId: string, productName: string) => Promise<boolean>
}

interface UseCompetitorProductActionsProps {
  onSuccess?: (data?: CompetitorProduct) => Promise<void> | void
}

export const useCompetitorProductActions = ({
  onSuccess,
}: UseCompetitorProductActionsProps = {}): UseCompetitorProductActionsReturn => {
  const { createCompetitorProduct, updateCompetitorProduct, deleteCompetitorProduct } = useCompetitorProduct()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const createCompetitorProductData: SubmitHandler<CompetitorProductCreateInput> = async (input) => {
    const { error, data } = await createCompetitorProduct({ input })

    const hasError =
      error?.message ||
      (data?.competitorProduct.create.__typename === "CompetitorProductCreateFailure" &&
        Boolean(data?.competitorProduct.create.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create competitor product",
      })
      return
    }

    const competitorProduct = (data?.competitorProduct.create as CompetitorProductCreateSuccess).competitorProduct
    await onSuccess?.(competitorProduct)

    notify({
      type: NotificationType.Success,
      silent: false,
      content: `New Competitor Product successfully created. ${FILE_UPLOAD_TAKES_TIME_MESSAGE}`,
    })
  }

  const updateCompetitorProductData: SubmitHandler<CompetitorProductUpdateInput> = async (input) => {
    const { error, data } = await updateCompetitorProduct({ input })

    const hasError =
      error?.message ||
      (data?.competitorProduct.update.__typename === "CompetitorProductUpdateFailure" &&
        Boolean(data?.competitorProduct.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update competitor product",
      })
      return
    }

    const competitorProduct = (data?.competitorProduct.update as CompetitorProductUpdateSuccess).competitorProduct
    await onSuccess?.(competitorProduct)

    notify({
      type: NotificationType.Success,
      silent: false,
      content: `${competitorProduct.productName} changes saved.`,
    })
  }

  const deleteCompetitorProductData = async (competitorProductId: string, productName: string): Promise<boolean> => {
    const { error, data } = await deleteCompetitorProduct({
      input: { competitorProductId },
    })

    const hasError =
      error?.message ||
      (data?.competitorProduct.delete.__typename === "CompetitorProductDeleteFailure" &&
        Boolean(data?.competitorProduct.delete.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to delete competitor product",
      })
      return false
    }

    await onSuccess?.()

    notify({
      type: NotificationType.Success,
      silent: false,
      content: `${productName} successfully removed.`,
    })

    return true
  }

  return {
    createCompetitorProductData,
    updateCompetitorProductData,
    deleteCompetitorProductData,
  }
}

export default useCompetitorProductActions
